import { scrub_url_string } from './privacy';

const object_type_name = '[object Object]';
const default_maximum_string_length = 2000;
const default_maximum_array_length = 50;
const default_maximum_object_keys = 100;
const default_maximum_json_depth = 5;

export const allowed_page_leave_reasons = new Set([
    'pagehide',
    'visibilitychange',
    'beforeunload',
    'unload',
    'spa_navigation',
    'unknown'
]);

const schema_field_maps = {
    metadata: {
        page_url: 'url',
        canonical_url: 'url',
        origin: 'string',
        protocol: 'string',
        host: 'string',
        pathname: 'string',
        search: 'string',
        hash: 'string',
        referer: 'url',
        referring_domain: 'string',
        page_title: 'string',
        page_description: 'string',
        og_title: 'string',
        og_description: 'string',
        og_image: 'url',
        content_language: 'string',
        url_params: 'object',
        utm_source: 'string',
        utm_medium: 'string',
        utm_campaign: 'string',
        utm_term: 'string',
        utm_content: 'string',
        utm_id: 'string',
        gclid: 'string',
        fbclid: 'string',
        msclkid: 'string',
        refcode: 'string',
        refcode2: 'string',
        refcodes: 'object',
        iterableCampaignId: 'integer',
        iterableTemplateId: 'integer',
        iterableCampaignId_first_seen_at: 'string',
        iterableCampaignId_last_seen_at: 'string',
        attribution_touch: 'string',
        geo_country_code: 'string',
        geo_region_code: 'string',
        geo_city: 'string',
        geo_timezone: 'string',
        network_asn: 'integer',
        network_organization: 'string',
        is_spa_navigation: 'boolean',
        navigation_type: 'string'
    },
    browser: {
        user_agent: 'string',
        browser_name: 'string',
        browser_version: 'string',
        browser_engine: 'string',
        os: 'string',
        os_version: 'string',
        device_type: 'string',
        device_vendor: 'string',
        device_model: 'string',
        viewport_width: 'integer',
        viewport_height: 'integer',
        screen_width: 'integer',
        screen_height: 'integer',
        screen_available_width: 'integer',
        screen_available_height: 'integer',
        device_width: 'integer',
        device_height: 'integer',
        device_pixel_ratio: 'number',
        color_depth: 'integer',
        pixel_depth: 'integer',
        orientation_type: 'string',
        orientation_angle: 'integer',
        language: 'string',
        languages: 'array',
        timezone: 'string',
        timezone_offset_minutes: 'integer',
        hardware_concurrency: 'integer',
        device_memory_gb: 'number',
        max_touch_points: 'integer',
        cookies_enabled: 'boolean',
        local_storage_available: 'boolean',
        session_storage_available: 'boolean',
        do_not_track: 'boolean',
        online: 'boolean',
        connection_effective_type: 'string',
        connection_downlink_mbps: 'number',
        connection_rtt_ms: 'integer',
        connection_save_data: 'boolean',
        prefers_reduced_motion: 'boolean',
        prefers_color_scheme: 'string',
        webdriver: 'boolean',
        visibility_state: 'string',
        lib: 'string',
        lib_version: 'string',
        init_started_at: 'string',
        init_completed_at: 'string',
        init_duration_ms: 'integer',
        consent_state: 'string'
    },
    element: {
        text: 'string',
        tag_name: 'string',
        attr_id: 'string',
        attr_class: 'string',
        attr_role: 'string',
        attr_href: 'url',
        attr_name: 'string',
        attr_type: 'string',
        attr_title: 'string',
        attr_aria_label: 'string',
        href_domain: 'string',
        href_pathname: 'string',
        is_same_origin_href: 'boolean',
        selector: 'string',
        position_x: 'integer',
        position_y: 'integer',
        page_x: 'integer',
        page_y: 'integer',
        bbox_left: 'integer',
        bbox_top: 'integer',
        bbox_right: 'integer',
        bbox_bottom: 'integer',
        bbox_width: 'integer',
        bbox_height: 'integer',
        scroll_x: 'integer',
        scroll_y: 'integer',
        nth_child: 'integer',
        nth_of_type: 'integer',
        dom_event: 'string',
        click_button: 'integer',
        click_count: 'integer',
        data_hub_value: 'number',
        data_hub_form_id: 'string',
        data_hub_form_name: 'string',
        data_hub_field_name: 'string',
        is_data_hub_form_element: 'boolean',
        is_outbound_link: 'boolean',
        is_download_link: 'boolean',
        closest_heading_text: 'string',
        closest_section_selector: 'string',
        attributes: 'object',
        elements_chain: 'array'
    },
    engagement: {
        scroll_depth_percent: 'integer',
        scroll_depth_px: 'integer',
        max_scroll_depth_percent: 'integer',
        page_height: 'integer',
        viewport_height: 'integer',
        active_time_ms: 'integer',
        visible_time_ms: 'integer',
        idle_time_ms: 'integer',
        ping_index: 'integer',
        is_final_ping: 'boolean',
        page_leave_reason: 'page_leave_reason'
    },
    performance: {
        time_to_first_byte_ms: 'integer',
        dom_content_loaded_ms: 'integer',
        load_event_ms: 'integer',
        first_contentful_paint_ms: 'integer',
        largest_contentful_paint_ms: 'integer',
        cumulative_layout_shift: 'number',
        interaction_to_next_paint_ms: 'integer',
        first_input_delay_ms: 'integer'
    },
    video: {
        video_id: 'string',
        video_provider: 'string',
        video_event_type: 'string',
        current_time_seconds: 'number',
        duration_seconds: 'number',
        percent_played: 'number',
        muted: 'boolean',
        volume: 'number',
        playback_rate: 'number',
        is_ad: 'boolean',
        ad_id: 'string',
        ssp_id: 'string'
    },
    commerce: {
        cart_id: 'string',
        cart_action: 'string',
        items: 'commerce_items'
    }
};

export function is_plain_object(value) {
    return Object.prototype.toString.call(value) === object_type_name;
}

/**
 * Converts an unknown value into a trimmed string with a maximum length, or null.
 *
 * @param {*} value - Unknown source value.
 * @param {number} maximum_length - Maximum returned string length.
 * @returns {string|null} Sanitized string or null.
 */
export function coerce_string_or_null(value, maximum_length = default_maximum_string_length) {
    try {
        if (value === null || value === undefined) {
            return null;
        }
        if (typeof value === 'string') {
            const trimmed_value = value.trim();
            return trimmed_value.length > 0 ? trimmed_value.slice(0, maximum_length) : null;
        }
        if (typeof value === 'number' || typeof value === 'boolean') {
            return String(value).slice(0, maximum_length);
        }
        return null;
    } catch {
        return null;
    }
}

export function coerce_number_or_null(value) {
    try {
        if (typeof value === 'number' && Number.isFinite(value)) {
            return value;
        }
        if (typeof value === 'string' && value.trim() !== '') {
            const parsed_value = Number(value);
            return Number.isFinite(parsed_value) ? parsed_value : null;
        }
        return null;
    } catch {
        return null;
    }
}

export function coerce_integer_or_null(value) {
    const parsed_value = coerce_number_or_null(value);
    if (parsed_value === null) {
        return null;
    }
    return Math.round(parsed_value);
}

export function coerce_boolean_or_null(value) {
    try {
        if (typeof value === 'boolean') {
            return value;
        }
        if (typeof value === 'string') {
            const normalized_value = value.trim().toLowerCase();
            if (['true', '1', 'yes', 'on'].includes(normalized_value)) {
                return true;
            }
            if (['false', '0', 'no', 'off'].includes(normalized_value)) {
                return false;
            }
        }
        if (typeof value === 'number') {
            if (value === 1) {
                return true;
            }
            if (value === 0) {
                return false;
            }
        }
        return null;
    } catch {
        return null;
    }
}

export function sanitize_page_leave_reason(page_leave_reason) {
    const normalized_reason = coerce_string_or_null(page_leave_reason, 100);
    if (!normalized_reason) {
        return 'unknown';
    }
    return allowed_page_leave_reasons.has(normalized_reason) ? normalized_reason : 'unknown';
}

export function sanitize_event_name(event_name) {
    const sanitized_event_name = coerce_string_or_null(event_name, 200);
    if (!sanitized_event_name) {
        return null;
    }
    return sanitized_event_name;
}

/**
 * Converts unknown JavaScript values into JSON-compatible values that can be safely
 * serialized for analytics. Circular references, DOM nodes, functions, and symbols are
 * dropped or stringified rather than allowed to break delivery.
 *
 * @param {*} value - Unknown input value.
 * @param {Object} options - Optional sanitization controls.
 * @param {WeakSet<object>} seen_objects - Internal circular-reference guard.
 * @returns {*} JSON-compatible value, or undefined when the value should be removed.
 */
export function sanitize_json_compatible_value(value, options = {}, seen_objects = new WeakSet()) {
    const maximum_depth = options.maximum_depth || default_maximum_json_depth;
    const maximum_array_length = options.maximum_array_length || default_maximum_array_length;
    const maximum_object_keys = options.maximum_object_keys || default_maximum_object_keys;
    const maximum_string_length = options.maximum_string_length || default_maximum_string_length;
    const current_depth = options.current_depth || 0;

    if (value === null || value === undefined) {
        return null;
    }
    if (typeof value === 'string') {
        return value.slice(0, maximum_string_length);
    }
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null;
    }
    if (typeof value === 'boolean') {
        return value;
    }
    if (typeof value === 'bigint') {
        return value.toString();
    }
    if (typeof value === 'function' || typeof value === 'symbol') {
        return undefined;
    }
    if (typeof Node !== 'undefined' && value instanceof Node) {
        return undefined;
    }
    if (current_depth >= maximum_depth) {
        return '[MaxDepth]';
    }
    if (Array.isArray(value)) {
        return value.slice(0, maximum_array_length)
            .map((item_value) => sanitize_json_compatible_value(item_value, {
                ...options,
                current_depth: current_depth + 1
            }, seen_objects))
            .filter((item_value) => item_value !== undefined);
    }
    if (typeof value === 'object') {
        if (seen_objects.has(value)) {
            return '[Circular]';
        }
        seen_objects.add(value);
        const output_object = {};
        Object.entries(value).slice(0, maximum_object_keys).forEach(([object_key, object_value]) => {
            const sanitized_key = coerce_string_or_null(object_key, 150);
            if (!sanitized_key) {
                return;
            }
            const sanitized_value = sanitize_json_compatible_value(object_value, {
                ...options,
                current_depth: current_depth + 1
            }, seen_objects);
            if (sanitized_value !== undefined) {
                output_object[sanitized_key] = sanitized_value;
            }
        });
        seen_objects.delete(value);
        return output_object;
    }
    return undefined;
}

function sanitize_commerce_item(item_value) {
    if (!is_plain_object(item_value)) {
        return null;
    }
    const sanitized_item = {
        categories: Array.isArray(item_value.categories)
            ? item_value.categories.map((category_value) => coerce_string_or_null(category_value, 300)).filter(Boolean).slice(0, 20)
            : [],
        item_data: sanitize_json_compatible_value(item_value.item_data ?? item_value.dataFields ?? null),
        description: coerce_string_or_null(item_value.description),
        id: coerce_string_or_null(item_value.id, 300),
        image_url: scrub_url_string(coerce_string_or_null(item_value.image_url || item_value.imageUrl)),
        name: coerce_string_or_null(item_value.name, 500),
        price: coerce_number_or_null(item_value.price),
        currency: coerce_string_or_null(item_value.currency, 10),
        quantity: coerce_number_or_null(item_value.quantity),
        sku: coerce_string_or_null(item_value.sku, 300),
        url: scrub_url_string(coerce_string_or_null(item_value.url))
    };
    Object.keys(sanitized_item).forEach((field_name) => {
        if (sanitized_item[field_name] === null || sanitized_item[field_name] === undefined) {
            delete sanitized_item[field_name];
        }
    });
    return Object.keys(sanitized_item).length > 0 ? sanitized_item : null;
}

function sanitize_value_by_type(value, value_type) {
    if (value === null || value === undefined) {
        return null;
    }
    if (value_type === 'url') {
        return scrub_url_string(coerce_string_or_null(value));
    }
    if (value_type === 'string') {
        return coerce_string_or_null(value);
    }
    if (value_type === 'number') {
        return coerce_number_or_null(value);
    }
    if (value_type === 'integer') {
        return coerce_integer_or_null(value);
    }
    if (value_type === 'boolean') {
        return coerce_boolean_or_null(value);
    }
    if (value_type === 'array') {
        return Array.isArray(value) ? sanitize_json_compatible_value(value) : null;
    }
    if (value_type === 'object') {
        return is_plain_object(value) ? sanitize_json_compatible_value(value) : null;
    }
    if (value_type === 'page_leave_reason') {
        return sanitize_page_leave_reason(value);
    }
    if (value_type === 'commerce_items') {
        if (!Array.isArray(value)) {
            return null;
        }
        const sanitized_items = value.map(sanitize_commerce_item).filter(Boolean).slice(0, 50);
        return sanitized_items.length > 0 ? sanitized_items : null;
    }
    return null;
}

function sanitize_structured_section(section_name, section_value) {
    if (!is_plain_object(section_value)) {
        return {
            sanitized_section: undefined,
            overflow_data: {}
        };
    }

    const schema_map = schema_field_maps[section_name] || {};
    const sanitized_section = {};
    const overflow_data = {};

    Object.entries(section_value).forEach(([field_name, field_value]) => {
        if (!Object.prototype.hasOwnProperty.call(schema_map, field_name)) {
            if (section_name === 'metadata') {
                const overflow_value = sanitize_json_compatible_value(field_value);
                if (overflow_value !== undefined) {
                    overflow_data[`${section_name}_${field_name}`] = overflow_value;
                }
            }
            return;
        }
        const sanitized_value = sanitize_value_by_type(field_value, schema_map[field_name]);
        if (sanitized_value !== null && sanitized_value !== undefined) {
            sanitized_section[field_name] = sanitized_value;
        }
    });

    return {
        sanitized_section: Object.keys(sanitized_section).length > 0 ? sanitized_section : undefined,
        overflow_data
    };
}

/**
 * Sanitizes the capture payload into the exact top-level sections expected by the
 * browser-to-/api/post/web contract. Known structured fields stay queryable. Unknown
 * top-level values move into web_event_data when they can be serialized safely.
 *
 * @param {Object} event_properties - Properties passed to data_hub.capture().
 * @returns {Object} Sanitized event payload sections.
 */
export function sanitize_structured_event_payload(event_properties = {}) {
    const source_properties = is_plain_object(event_properties) ? event_properties : {};
    const sanitized_payload = {};
    const web_event_data = is_plain_object(source_properties.web_event_data)
        ? sanitize_json_compatible_value(source_properties.web_event_data)
        : {};

    ['metadata', 'browser', 'element', 'engagement', 'performance', 'video', 'commerce'].forEach((section_name) => {
        if (!Object.prototype.hasOwnProperty.call(source_properties, section_name)) {
            return;
        }
        const { sanitized_section, overflow_data } = sanitize_structured_section(
            section_name,
            source_properties[section_name]
        );
        if (sanitized_section !== undefined) {
            sanitized_payload[section_name] = sanitized_section;
        }
        Object.assign(web_event_data, overflow_data);
    });

    const top_level_reserved_keys = new Set([
        'metadata',
        'browser',
        'element',
        'engagement',
        'performance',
        'video',
        'commerce',
        'web_event_data',
        'event_sub_type'
    ]);

    Object.entries(source_properties).forEach(([property_name, property_value]) => {
        if (top_level_reserved_keys.has(property_name)) {
            return;
        }
        const sanitized_property_name = coerce_string_or_null(property_name, 150);
        const sanitized_property_value = sanitize_json_compatible_value(property_value);
        if (sanitized_property_name && sanitized_property_value !== undefined) {
            web_event_data[sanitized_property_name] = sanitized_property_value;
        }
    });

    if (web_event_data && Object.keys(web_event_data).length > 0) {
        sanitized_payload.web_event_data = web_event_data;
    }
    sanitized_payload.event_sub_type = coerce_string_or_null(source_properties.event_sub_type, 200);

    return sanitized_payload;
}